export type WebsiteLogoLocaleEntry = {
    url?: string;
    alt?: string;
    link?: { url?: string; isOpenInNewTab?: boolean };
};

export type WebsiteAssetsLogo = {
    topPanel?: Partial<Record<string, WebsiteLogoLocaleEntry>>;
    footer?: Partial<Record<string, WebsiteLogoLocaleEntry>>;
};

/** Pick url/alt from a localized media slot (preferred lang, else any non-empty url). */
export function pickFromLocalizedSlot(
    slot: Partial<Record<string, WebsiteLogoLocaleEntry>> | undefined,
    lang: string,
): { url: string | null; alt: string | null } {
    if (!slot || typeof slot !== 'object') {
        return { url: null, alt: null };
    }
    const preferred = slot[lang];
    if (typeof preferred?.url === 'string' && preferred.url.length > 0) {
        return { url: preferred.url, alt: preferred.alt ?? null };
    }
    for (const entry of Object.values(slot)) {
        if (typeof entry?.url === 'string' && entry.url.length > 0) {
            return { url: entry.url, alt: entry.alt ?? null };
        }
    }
    return { url: null, alt: null };
}

/**
 * Resolve logo URLs from config, falling back to venue LOGO asset.
 * If neither config nor venue provides a logo, src will be null.
 */
export function resolveWebsiteLogos(
    config: Record<string, unknown> | null | undefined,
    lang: string,
    venueLogoUrl?: string | null,
): {
    top: { src: string | null; alt: string | null };
    footer: { src: string | null; alt: string | null };
} {
    const assets = config?.assets as { logo?: WebsiteAssetsLogo } | undefined;

    const top = pickFromLocalizedSlot(assets?.logo?.topPanel, lang);
    const footer = pickFromLocalizedSlot(assets?.logo?.footer, lang);

    const fallback = venueLogoUrl ?? null;

    return {
        top: { src: top.url ?? fallback, alt: top.alt },
        footer: { src: footer.url ?? fallback, alt: footer.alt },
    };
}
